import React, { useState, FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RadioTowerIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, AlertTriangleIcon, Loader2Icon, ArrowRightIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
export function Login() {
  const {
    session,
    loading: authLoading,
    signIn
  } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  if (!authLoading && session) {
    const from = (location.state as any)?.from?.pathname || '/';
    return <Navigate to={from} replace data-id="element-849" data-mp="3mco2gp5fxo9m" />;
  }
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Email dan password wajib diisi.');
      return;
    }
    setSubmitting(true);
    const {
      error: signInError
    } = await signIn(email, password);
    setSubmitting(false);
    if (signInError) {
      setError(signInError.toLowerCase().includes('invalid') ? 'Email atau password salah.' : signInError);
    }
  };
  return <div className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden" data-id="element-850" data-mp="2we2dla8g7jyb">
      {/* Ambient glows */}
      <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-cyan-500/10 rounded-full blur-[140px] pointer-events-none" data-id="element-851" data-mp="358pebt363848" />
      <div className="absolute bottom-[-25%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[130px] pointer-events-none" data-id="element-852" data-mp="3k4ydyvqyznh6" />

      <motion.div initial={{
      opacity: 0,
      y: 22
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55,
      ease: [0.16, 1, 0.3, 1]
    }} className="w-full max-w-md relative z-10" data-id="element-853" data-mp="1prf7gweyoujq">
        {/* Brand */}
        <div className="flex flex-col items-center mb-8" data-id="element-854" data-mp="2ai2m8qt8l20l">
          <div className="relative mb-4" data-id="element-855" data-mp="2ei35qpv703ei">
            <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-glow-strong flex items-center justify-center" data-id="element-856" data-mp="2eiuylsxenn13">
          <img src="https://fzkptteqaooavawzwwlu.supabase.co/storage/v1/object/public/Asset%20Raptor-tif/raptor-tif-web-sidebar-tile.jpg" alt="RAPTOR-TIF" className="w-full h-full object-cover" data-id="element-857" data-mp="2jjiogb9wdxtq" />
          </div>
            <div className="absolute -inset-2 rounded-2xl bg-cyan-400/20 blur-xl -z-10 animate-breathe" data-id="element-858" data-mp="2xg5myihi0w1r" />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-[0.22em]" data-id="element-859" data-mp="328gwmd21jw7i">
            RAPTOR<span className="text-cyan-400" data-id="element-860" data-mp="375yzblpgtf72">-TIF</span>
          </h1>
          <p className="text-xs text-slate-400 mt-2 text-center" data-id="element-861" data-mp="3c6d32rk1a2za">
            Radio Planning Tools, Re‑engineering &amp; Inventory TIF
          </p>
          <p className="text-[10px] text-cyan-400/70 font-mono tracking-[0.18em] uppercase mt-2" data-id="element-862" data-mp="3h5puzqpri08q">
            Network Planning · 2026
          </p>
        </div>

        {/* Card */}
<div className="glass-panel border-shimmer rounded-2xl p-6 md:p-8" data-id="element-863" data-mp="10hq5ptlkd57t">
  <div className="mb-6 text-center" data-id="element-864" data-mp="vjevviv9qupl">
    <h2 className="text-lg font-semibold text-white" data-id="element-865" data-mp="vkgafxbhtzc2">
      Sign in
    </h2>
    <p className="text-xs text-slate-500 mt-1" data-id="element-866" data-mp="qldfj5p899ht">
      Welcome Back, Sign in to access your dashboard!
    </p>
  </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate data-id="element-867" data-mp="1o712z13pghh7">
            {/* Email */}
            <div className="flex flex-col gap-1.5" data-id="element-868" data-mp="1j9rebno549uf">
              <label htmlFor="email" className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]" data-id="element-869" data-mp="1ei9ej2xusfb1">
                Email
              </label>
              <div className="relative" data-id="element-870" data-mp="26zdeadcc6t3h">
                <MailIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-871" data-mp="270eqil539lms" />
                <input id="email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="nama@perusahaan.com" disabled={submitting} className="input-dark w-full pl-10 text-sm disabled:opacity-60" data-id="element-872" data-mp="1mp2h9d8a2fa2" />
              </div>
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5" data-id="element-873" data-mp="2ag7tzjix8rg5">
              <label htmlFor="password" className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]" data-id="element-874" data-mp="2aewnhby3d5lc">
                Password
              </label>
              <div className="relative" data-id="element-875" data-mp="20nijaqv73hlv">
                <LockIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-876" data-mp="2of7fiib50q83" />
                <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" disabled={submitting} className="input-dark w-full pl-10 pr-10 text-sm disabled:opacity-60" data-id="element-877" data-mp="hqxg7fkn8god" />
                <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300 transition-colors" data-id="element-878" data-mp="3uy8ts7dvn8l4">
                  {showPassword ? <EyeOffIcon size={16} data-id="element-879" data-mp="1j7eongffff0h" /> : <EyeIcon size={16} data-id="element-880" data-mp="1j6lfy9wwqffp" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && <motion.div initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} role="alert" className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs" data-id="element-881" data-mp="bvxd44tqtted">
                <AlertTriangleIcon size={14} className="mt-0.5 flex-shrink-0" data-id="element-882" data-mp="pvygps1e7exj" />
                <span data-id="element-883" data-mp="3pak03s4y1vm3">{error}</span>
              </motion.div>}

            {/* Submit */}
            <button type="submit" disabled={submitting} className="btn-primary w-full flex items-center justify-center gap-2 text-sm text-white disabled:opacity-70 disabled:cursor-not-allowed mt-2" data-id="element-884" data-mp="3u9o8whnyhrbw">
              {submitting ? <>
                  <Loader2Icon size={16} className="animate-spin" data-id="element-885" data-mp="36kbzphsmpj9l" />
                  <span data-id="element-886" data-mp="36hzn7k5n413x">Memverifikasi…</span>
                </> : <>
                  <span data-id="element-887" data-mp="3ajkxcxqsi1af">Sign in</span>
                  <ArrowRightIcon size={16} data-id="element-888" data-mp="332oazw2wf35a" />
                </>}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-800/70" data-id="element-889" data-mp="2y44q7z01jxei">
 <p className="text-[11px] text-slate-400 text-center leading-relaxed" data-id="element-890" data-mp="2u1gf1knuueed">
  <span className="font-semibold text-slate-200" data-id="element-891" data-mp="2tzn7m35f3q5b">
    Access is restricted to registered accounts.
  </span>
  <br data-id="element-892" data-mp="2p4hk4zwda3f4" />
  Please contact{" "}
  <span className="font-semibold text-slate-300" data-id="element-893" data-mp="2k30nxhotv54v">
    Fajar Hidayat - 082122299842
  </span>
  <br data-id="element-894" data-mp="2fck5tka2avc6" />
  <span className="font-bold text-cyan-400" data-id="element-895" data-mp="2fctzbhubfkmm">
    Network Planning - Access Planning Area TIF
  </span>
  <br data-id="element-896" data-mp="2acz9lhma1135" />
  to request a new account.
</p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-1 mt-6" data-id="element-897" data-mp="26bvuixc1z92z">
  <div className="flex items-center justify-center gap-2" data-id="element-898" data-mp="26dqf2ajewz6j">
    <span className="pulse-dot bg-emerald-400 text-emerald-400" data-id="element-899" data-mp="21d2h14wrni2t" />
    <span className="text-[10px] text-slate-600 font-mono tracking-wider uppercase" data-id="element-900" data-mp="1wdpsmfq8y2y4">
      Secure connection · Supabase Auth
    </span>
  </div>

  <span className="text-[6px] text-slate-500 font-mono tracking-widest uppercase opacity-70" data-id="element-901" data-mp="3kbgx35bt8jaf">
    Created by Teguh Febriyanto
  </span>
</div>
      </motion.div>
    </div>;
}